import { IInvoice } from '@root/query'
import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { formatDate, getOptionLabelByValue } from '@root/utils'
import { INVOICE_REPETITION_MONTHS, InvoiceRepetitionDays, InvoiceStatus } from '@root/constants'
import { Alert, TextTooltip } from '@root/components'
import { FaQuestionCircle } from 'react-icons/fa'
import { RecurringInvoiceSettingBadgeButton } from './InvoiceStatusBadgeButton'
import InvoiceRecurrenceEditSettings from './InvoiceRecurrenceEditSettings'
import { PermissionBoundary, PermissionSet } from '@root/context'
import { useInvoice } from '@root/query/hooks/invoices'

interface Props {
  invoice: IInvoice
}

const InvoiceRecurrenceInfo: React.FC<Props> = ({ invoice }) => {
  const [t, i18n] = useTranslation()
  const [showRecurenceEdit, setShowRecurenceEdit] = useState(false)

  const { data: parentInvoice } = useInvoice(
    { invoiceId: invoice.recurrence_parent_id },
    { enabled: Boolean(invoice.recurrence_parent_id) }
  )

  const invoiceToUse = parentInvoice ?? invoice

  const dates = invoiceToUse?.next_recurrences

  const showThreshHold = 3
  const amountOfToShow = dates.length < showThreshHold ? dates.length : showThreshHold

  const getMatchingTranslatedMonth = (month: number): string => {
    const result = getOptionLabelByValue(INVOICE_REPETITION_MONTHS, month, {
      t,
      fallback: '',
      fallbackToValue: false
    })
    if (i18n.language === 'en') {
      return result
    }
    return result.toLowerCase()
  }

  const createInvoiceRepetitionElement = (invoice: IInvoice): React.ReactElement | null => {
    const rule = invoice?.recurrence_rule?.rule
    if (!rule) return null

    const dayNumber = rule.by_month_day[0]
    const monthNumber = rule.by_month[0]
    const interval = rule.interval ?? 1
    const isLastDay = dayNumber === InvoiceRepetitionDays.DAY_LAST
    const isMonthly = rule.by_month.length === 0

    let translationKey: string
    let translationValues: Record<string, unknown> | undefined

    if (isMonthly) {
      if (interval > 1) {
        translationKey = isLastDay
          ? 'invoicing.details.recurrence.intervalMonthlyLastDay'
          : 'invoicing.details.recurrence.intervalMonthlyNormalDay'
        translationValues = isLastDay ? { interval } : { interval, count: dayNumber, ordinal: true }
      } else {
        translationKey = isLastDay
          ? 'invoicing.details.recurrence.monthlyLastDay'
          : 'invoicing.details.recurrence.monthlyNormalDay'
        translationValues = isLastDay ? undefined : { count: dayNumber, ordinal: true }
      }
    } else if (rule.by_month_day.length > 0) {
      const month = getMatchingTranslatedMonth(monthNumber)
      translationKey = isLastDay
        ? 'invoicing.details.recurrence.yearlyLastDay'
        : 'invoicing.details.recurrence.yearlyNormalDay'
      translationValues = isLastDay ? { month } : { month, count: dayNumber, ordinal: true }
    } else {
      return null
    }

    return <Trans>{t(translationKey, translationValues)}</Trans>
  }

  const getTheNextInvoicesToBeSent = (amountOfToShow: number, invoice: IInvoice): Array<string> => {
    const dates = invoice?.next_recurrences.slice(0, amountOfToShow) ?? []
    return dates.map((date, index) =>
      index === dates.length - 1 ? `${formatDate(date)}.` : `${formatDate(date)}, `
    )
  }

  const createInvoiceEndingDate = (invoice: IInvoice): string => {
    if (!invoice?.recurrence_end) {
      return `${t('invoicing.details.recurrence.invoiceDoesntEnd')}`
    }
    const date = formatDate(invoice?.recurrence_end)
    return `${t('invoicing.details.recurrence.invoiceEnds')} ${date}.`
  }

  const createNextInvoiceString = (invoice: IInvoice): string => {
    if (invoice?.next_recurrences.length === 0) {
      return `${t('invoicing.details.recurrence.guidanceTextForLastInvoice')}`
    }

    if (invoice.status === InvoiceStatus.DRAFT) {
      return `${t('invoicing.details.recurrence.guidanceDraftTextForNextInvoice')} ${formatDate(
        invoice?.next_recurrences[0]
      )}.`
    }
    return `${t('invoicing.details.recurrence.guidanceTextForNextInvoice')} ${formatDate(
      invoice?.next_recurrences[0]
    )}.`
  }

  const createToolTipMessage = (amountOfToShow: number, invoice: IInvoice) => {
    if (amountOfToShow === 0) {
      return t('invoicing.details.recurrence.guidanceTextForLastInvoice')
    }

    const formattedDates = getTheNextInvoicesToBeSent(amountOfToShow, invoice).join('')
    return `${t('invoicing.details.recurrence.nextInvoicesToolTip', {
      amountOfToShow
    })} ${formattedDates}`
  }

  const hasRecurrenceEnded = invoiceToUse?.is_recurrence_end_exceeded

  return (
    <>
      <Alert
        type="info"
        marginBottom={0}
        title={
          <HeaderRow>
            <span>{t('invoicing.details.recurrence.recurringInvoice')}</span>
            <TextTooltip tooltip={createToolTipMessage(amountOfToShow, invoiceToUse)}>
              <FaQuestionCircle size={14} />
            </TextTooltip>
          </HeaderRow>
        }
        description={
          <BannerContent>
            {hasRecurrenceEnded ? (
              <p>
                {t('invoicing.details.recurrence.hasEnded', {
                  recurrenceEndDate: formatDate(invoiceToUse.recurrence_end)
                })}
              </p>
            ) : (
              <>
                <p>
                  {createInvoiceRepetitionElement(invoiceToUse)}{' '}
                  {createInvoiceEndingDate(invoiceToUse)}
                </p>
                <p>{createNextInvoiceString(invoiceToUse)}</p>
                <PermissionBoundary required={PermissionSet.INVOICING_EDIT}>
                  <RecurringInvoiceSettingBadgeButton onClick={() => setShowRecurenceEdit(true)} />
                </PermissionBoundary>
              </>
            )}
          </BannerContent>
        }
      />

      <InvoiceRecurrenceEditSettings
        invoice={invoice}
        handleOnClose={() => setShowRecurenceEdit(false)}
        showPrompt={showRecurenceEdit}
      />
    </>
  )
}

export default InvoiceRecurrenceInfo

const BannerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;

  p {
    margin: 0;
    font-size: ${({ theme }) => theme.fontSize.sm}rem;
    line-height: 1.5;
  }
`

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`
